"use client";

import { c as _c } from "react/compiler-runtime";
import { Search } from "lucide-react";
import { useLabels } from "@/lib/labels/LabelsProvider";
interface EmptyStateProps {
  variant: "initial" | "no-results" | "loading";
  query?: string;
  onSuggestionClick?: (q: string) => void;
}
export function EmptyState(t0) {
  const $ = _c(25);
  const {
    variant,
    query,
    onSuggestionClick
  } = t0;
  const labels = useLabels();
  if (variant === "loading") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <EntitySkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (variant === "no-results") {
    const t1 = labels.search?.noResults || "No results for \"{query}\"";
    let t2;
    if ($[1] !== query || $[2] !== t1) {
      t2 = t1.replace("{query}", query || "");
      $[1] = query;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const text = t2;
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4"><Search className="w-7 h-7 text-muted-foreground" /></div>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== text) {
      t4 = <div className="mt-16 flex flex-col items-center text-center">{t3}<p className="text-base text-muted-foreground">{text}</p></div>;
      $[5] = text;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  const t1 = labels.search?.tryExample1 || "Barcelona";
  const t2 = labels.search?.tryExample2 || "Manchester United";
  const t3 = labels.search?.tryExample3 || "Real Madrid";
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = [t1, t2, t3];
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const suggestions = t4;
  const tryLabel = labels.search?.try || "Try";
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4"><Search className="w-7 h-7 text-muted-foreground" /></div>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = labels.search?.initialPrompt || "Search teams, players, competitions and articles";
  let t7;
  if ($[12] !== t6) {
    t7 = <p className="text-base text-muted-foreground mb-6">{t6}</p>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== tryLabel) {
    t8 = <span className="text-xs text-muted-foreground uppercase tracking-wider">{tryLabel}</span>;
    $[14] = tryLabel;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== onSuggestionClick || $[17] !== suggestions) {
    t9 = suggestions.map(s => <button key={s} type="button" onClick={() => onSuggestionClick?.(s)} className="px-3 py-1.5 text-sm rounded-full border border-border bg-card hover:bg-muted hover:border-primary/40 transition cursor-pointer">{s}</button>);
    $[16] = onSuggestionClick;
    $[17] = suggestions;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="flex flex-wrap items-center justify-center gap-2">{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t7) {
    t11 = <div className="mt-16 flex flex-col items-center text-center">{t5}{t7}{t10}</div>;
    $[22] = t10;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
function EntitySkeleton() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-3 w-20 bg-muted rounded mb-3 animate-pulse" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div>{t0}<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{[0, 1, 2].map(_temp)}</div></div>;
    t2 = <div className="h-3 w-20 bg-muted rounded mb-3 animate-pulse" />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mt-8 space-y-8">{t1}<div>{t2}<div className="space-y-3">{[0, 1, 2, 3].map(_temp2)}</div></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function _temp2(i_0) {
  return <div key={i_0} className="h-20 border-b border-border bg-muted/30 rounded animate-pulse" />;
}
function _temp(i) {
  return <div key={i} className="h-20 border border-border rounded-lg bg-card animate-pulse" />;
}